import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button } from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, analytics } from "common/analytics";
import type { EvidenceType } from "model";
import {
  FILE_UPLOAD_TYPE,
  useCreateActionFileEntry,
  useCreateFileEntry,
} from "model";
import { useRef } from "react";
import { uploadFile } from "services";
import { useAppStore } from "stores";

// Detect file type from binary content (magic bytes) and fix the extension.
// Android WebView often reports both file.name and file.type incorrectly
// for content:// URIs, so we inspect the actual bytes instead.
const MAGIC_BYTES: Array<{ prefix: string; ext: string }> = [
  { prefix: "JVBERi", ext: ".pdf" }, // %PDF
  { prefix: "UEsDB", ext: ".zip" }, // PK (zip/docx/xlsx)
  { prefix: "/9j/", ext: ".jpg" }, // JPEG
  { prefix: "iVBORw", ext: ".png" }, // PNG
];

function fixFileExtension(name: string, base64: string): string {
  for (const { prefix, ext } of MAGIC_BYTES) {
    if (base64.startsWith(prefix)) {
      if (name.toLowerCase().endsWith(ext)) return name;
      const dotIdx = name.lastIndexOf(".");
      const base = dotIdx > 0 ? name.slice(0, dotIdx) : name;
      return base + ext;
    }
  }
  return name;
}

interface Props {
  actionSubmissionGuid: string;
  evidenceType?: EvidenceType;
}

export default function ActionEvidenceUpload({
  actionSubmissionGuid,
  evidenceType,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { notify } = useNotification();
  const user = useAppStore((state) => state.user);
  const createFileEntry = useCreateFileEntry();
  const createActionFileEntry = useCreateActionFileEntry();

  const uploadEvidence = async (
    fileName: string,
    base64: string,
    uploadType: FILE_UPLOAD_TYPE
  ) => {
    if (!user?.guid) return;

    try {
      const fileUpload = await uploadFile({ fileName, file: base64 });

      const fileEntry = await createFileEntry.mutateAsync({
        fileDescription: "",
        fileUploadId: fileUpload.guid,
        associatedUserId: user.guid,
        fileUploadType: uploadType,
        userId: user.guid,
      });

      await createActionFileEntry.mutateAsync({
        fileEntryGuid: fileEntry.guid,
        actionSubmissionGuid,
        fileName,
        fileUploadType: uploadType,
        fileEntryFileUploadGuid: fileUpload.guid,
        uploaderGuid: user.guid,
      });
      analytics.track(AnalyticsEvents.EVIDENCE_FILE_UPLOADED, {
        evidenceType,
        actionSubmissionGuid,
      });

      notify("Evidence uploaded successfully");
    } catch (err) {
      console.error("Failed to upload evidence:", err);
      notify("Failed to upload evidence", "error");
    }
  };

  const handleCameraPhoto = async (source: CameraSource) => {
    try {
      const image = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source,
        quality: 90,
      });
      if (!image.dataUrl) return;
      const base64 = image.dataUrl.split(",")[1];
      const fileName = `evidence_${Date.now()}.${image.format || "jpeg"}`;
      await uploadEvidence(
        fileName,
        base64,
        FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO
      );
    } catch (err) {
      // User cancelled photo selection — not an error
      if (err instanceof Error && err.message?.includes("cancel")) return;
      console.error("Failed to capture photo:", err);
      notify("Failed to capture photo", "error");
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    // Android WebView may return wrong name/type for content:// URIs.
    // Detect the real file type from the binary content (magic bytes).
    const fileName = fixFileExtension(file.name, base64);
    await uploadEvidence(
      fileName,
      base64,
      FILE_UPLOAD_TYPE.ACTION_EVIDENCE_FILE
    );
    e.target.value = "";
  };

  const isUploading =
    createFileEntry.isPending || createActionFileEntry.isPending;

  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileChange}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddPhotoAlternateIcon />}
        onClick={() => handleCameraPhoto(CameraSource.Photos)}
        disabled={isUploading}
      >
        Select Photo
      </Button>
      {Capacitor.isNativePlatform() && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<CameraAltIcon />}
          onClick={() => handleCameraPhoto(CameraSource.Camera)}
          disabled={isUploading}
        >
          Take Photo
        </Button>
      )}
      <Button
        variant="outlined"
        size="small"
        startIcon={<UploadFileIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
      >
        Add File
      </Button>
    </Box>
  );
}
